import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { Button } from '@/components/ui/button/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

import { useEditSupplier } from '../api/supplier-mutations';
import { Supplier } from '../types/supplier-types';

const editSupplierSchema = z.object({
  title: z.string().min(1, 'Supplier name is required'),
});

type EditSupplierFormData = z.infer<typeof editSupplierSchema>;

interface EditSupplierDialogProps {
  supplier: Supplier | null;
  onClose: () => void;
}

export function EditSupplierDialog({
  supplier,
  onClose,
}: EditSupplierDialogProps) {
  const editSupplier = useEditSupplier();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<EditSupplierFormData>({
    resolver: zodResolver(editSupplierSchema),
    defaultValues: {
      title: supplier?.title ?? '',
    },
  });

  const onSubmit = async (values: EditSupplierFormData) => {
    if (!supplier) return;
    await editSupplier.mutateAsync({
      id: supplier.id,
      title: values.title,
    });
    onClose();
  };

  useEffect(() => {
    reset({
      title: supplier?.title ?? '',
    });
  }, [supplier, reset]);

  return (
    <Dialog open={!!supplier} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit Supplier</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="title" className="text-right">
                Name
              </Label>
              <Input
                id="title"
                className="col-span-3"
                type="text"
                autoComplete="off"
                {...register('title', { required: 'This field is required' })}
              />
            </div>
            {errors.title && (
              <span className="text-sm text-destructive">
                {errors.title.message}
              </span>
            )}
          </div>
          <DialogFooter>
            <Button type="submit" disabled={editSupplier.isPending}>
              {editSupplier.isPending ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
